import { useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { AuthDialog } from "@/components/auth/AuthDialog";
import { IdleFilmPopup } from "@/components/media/IdleFilmPopup";
import { ConciergeProvider } from "@/context/ConciergeContext";
import { ConciergeLauncher } from "@/components/concierge/ConciergeLauncher";
import { ConciergePanel } from "@/components/concierge/ConciergePanel";
import { SplashScreen } from "@/components/media/SplashScreen";
import { installClickSounds } from "@/lib/sounds";
import Layout from "@/components/layout/Layout";
import HomePage from "@/pages/HomePage";
import SearchPage from "@/pages/SearchPage";
import ListingPage from "@/pages/ListingPage";
import BasesPage from "@/pages/BasesPage";
import BaseDetailPage from "@/pages/BaseDetailPage";
import MilitaryPage from "@/pages/MilitaryPage";
import BuyersPage from "@/pages/BuyersPage";
import SellersPage from "@/pages/SellersPage";
import ToolsPage from "@/pages/ToolsPage";
import NeighborhoodExplorerPage from "@/pages/NeighborhoodExplorerPage";
import OpenHousesPage from "@/pages/OpenHousesPage";
import GuidePage from "@/pages/GuidePage";
import AboutPage from "@/pages/AboutPage";
import ContactPage from "@/pages/ContactPage";
import AdminLoginPage from "@/pages/AdminLoginPage";
import AdminPage from "@/pages/AdminPage";
import CRMPage from "@/pages/CRMPage";
import AgentInvitePage from "@/pages/AgentInvitePage";
import AgentMiniSitePage from "@/pages/AgentMiniSitePage";
import NotFoundPage from "@/pages/NotFoundPage";

const Protected = ({ children }) => {
  const { user } = useAuth();
  if (user === null) return <div className="grid min-h-screen place-items-center text-neutral-500" data-testid="auth-checking">Checking access…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;
  if (!['admin', 'agent'].includes(user.role)) return <Navigate to="/" replace />;
  return children;
};

function App() {
  useEffect(() => installClickSounds(), []);
  return (
    <AuthProvider>
      <ConciergeProvider>
      <BrowserRouter>
        <Routes>
          <Route element={<Layout />}>
            <Route path="/" element={<HomePage />} />
            <Route path="/search" element={<SearchPage />} />
            <Route path="/listing/:key" element={<ListingPage />} />
            <Route path="/bases" element={<BasesPage />} />
            <Route path="/bases/:id" element={<BaseDetailPage />} />
            <Route path="/military" element={<MilitaryPage />} />
            <Route path="/buyers" element={<BuyersPage />} />
            <Route path="/sellers" element={<SellersPage />} />
            <Route path="/tools" element={<ToolsPage />} />
            <Route path="/neighborhoods" element={<NeighborhoodExplorerPage />} />
            <Route path="/open-houses" element={<OpenHousesPage />} />
            <Route path="/guides/:slug" element={<GuidePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Route>
          <Route path="/admin/login" element={<AdminLoginPage />} />
          <Route path="/admin" element={<Protected><AdminPage /></Protected>} />
          <Route path="/crm" element={<Protected><CRMPage /></Protected>} />
          <Route path="/agent-invite/:token" element={<AgentInvitePage />} />
          <Route path="/a/:slug" element={<AgentMiniSitePage />} />
          <Route path="/go/:slug" element={<AgentMiniSitePage campaign />} />
        </Routes>
        <AuthDialog />
        <IdleFilmPopup />
        <ConciergeLauncher />
        <ConciergePanel />
        <SplashScreen />
      </BrowserRouter>
      </ConciergeProvider>
      <Toaster position="bottom-right" richColors closeButton />
    </AuthProvider>
  );
}

export default App;
